import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getGuidedSessionDetail, type GuidedSessionDetail as GuidedSessionDetailData } from '../services/guidedSessionService';
import GuidedSessionBreakdown from '../components/GuidedSessionBreakdown';
import MarkdownExplanation from '../components/MarkdownExplanation';
import { ArrowLeft } from 'lucide-react';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function GuidedSessionDetail() {
  const { session, isLoading } = useAuth();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [detail, setDetail] = useState<GuidedSessionDetailData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session || !sessionId) return;
    getGuidedSessionDetail(session.student_id, parseInt(sessionId, 10)).then((data) => {
      setDetail(data);
      setLoading(false);
    });
  }, [session?.student_id, sessionId]);

  if (isLoading || loading) {
    return (
      <main className="max-w-2xl mx-auto px-4 pt-24 pb-8 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-coral-warm mx-auto mb-4" />
        <p className="text-gray-500 text-sm">Loading session…</p>
      </main>
    );
  }

  if (!detail || !detail.success) {
    return (
      <main className="max-w-2xl mx-auto px-4 pt-24 pb-8 text-center">
        <p className="text-gray-600 mb-4">Couldn't find that session.</p>
        <Link to="/understand-something/history" className="text-coral-warm font-medium hover:underline">
          Back to Past Sessions
        </Link>
      </main>
    );
  }

  return (
    <main className="max-w-2xl mx-auto w-full px-4 sm:px-6 pt-24 pb-8">
      <Link to="/understand-something/history" className="inline-flex items-center gap-1.5 text-sm text-coral-warm hover:text-orange-600 font-medium mb-4">
        <ArrowLeft className="w-4 h-4" />
        Back to Past Sessions
      </Link>

      <h1 className="text-xl font-bold text-ocean-deep mb-1">{detail.concept_name}</h1>
      <p className="text-sm text-gray-500 mb-6">{formatDate(detail.updated_at)}</p>

      {detail.conclude_message && (
        <div className="bg-orange-50 text-ocean-deep p-4 rounded-lg mb-6">
          <MarkdownExplanation text={detail.conclude_message} variant="light" />
        </div>
      )}

      <GuidedSessionBreakdown entries={detail.question_history} />
    </main>
  );
}
